/**
 * This Wizard component is very similar to the one in shopping-pwa and mainly contains:
 * - A start and end date input/picker (datepicker)
 * - A guests and rooms picker
 * - A submit button
 * All of which are tracked. When the user chooses their dates/guests/rooms and clicks
 * submit, they get sent to HSR.
 * TODO: Clean up experiment Experiment.Blossom_Date_Intervals_Mobile_HTG once it
 * reaches completion
 */
import * as React from "react";
import {
  FormFieldModel,
  GuestDialogProps,
  HiddenSearchDataProps,
  SavedWizardInputs,
  TypeaheadSelectionProps,
  WizardHotelPWAProps
} from "./typings";
import { inject, observer } from "mobx-react";
import { action, computed, observable } from "mobx";
import { formatDate } from "bernie-l10n";
import { QueryRoute } from "bernie-core";
import { ClientLogger } from "bernie-client";
import { Layer, ViewMedium, Viewport, ViewSmall, ViewLarge, ViewSizes } from "bernie-view";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { WizardDialogTrigger } from "./components/WizardDialogTrigger";
import { DestinationField } from "./components/DestinationField";
import { GuestDialog, GuestField } from "./components/GuestField";
import { HiddenSearchData } from "./components/HiddenSearchData";
import { SubmitButton } from "./components/SubmitButton";
import { UitkDatePickerTriggeredSelector } from "uitk-react-date-picker";
import { AddFlight } from "./components/AddFlight";
import { AirlineChildSeating, AirlineChildSeatingOptions } from "./components/AirlineChildSeating";
import { locKeys } from "./components/l10n";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent,
  sendImmediateTrackEvent
} from "components/utility/analytics/FlexAnalyticsUtils";
import { AddCar } from "./components/AddCar";
import { SearchForm } from "./components/SearchForm";
import { OriginField } from "./components/OriginField";
import { setCookie } from "src/components/utility/cookie";
import { PackageDisabledMessage } from "./components/PackageDisabledMessage";
import {
  getHSRFilterCode,
  getHSRStarRatingCode,
  getHSRFilters,
  getPropertyTypeCode
} from "src/components/utility/ContextUtil";
import { Experiment, isVariantEnabled } from "../../utility/experiment";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { NewDatePicker } from "./components/NewDatePicker";
import { DateFields, DatePickerDialog } from "./components/DateFields";
import { UitkNewDatePickerTriggeredSelector } from "uitk-react-new-date-picker";
import { EssClientLogger } from "typeahead-client-logger";
import { StickyCTAMobile } from "./components/StickyCTAMobile";

enum CookieKeys {
  WIZARD_HOTEL = "wizardHotel"
}

enum PageType {
  AIRPORT = "airport",
  POI = "point_of_interest"
}

enum StickWhenType {
  WILL_EXIT = "willExit",
  HAS_EXITED = "hasExited"
}

const SORT_DISTANCE_VALUE = "distance";

interface WizardHotelPWAState {
  startInvalidMessage: string;
  endInvalidMessage: string;
}

@inject("analytics", "routeStore", "context", "cookies", "compositionStore")
@observer
export class WizardHotelPWA extends React.Component<WizardHotelPWAProps> {
  @observable
  private startDate = "";
  @observable
  private endDate = "";
  @observable
  private guestsVal = "";
  @observable
  private adults = "2"; // Example: "2", "2,1,2" (represents each room's total adults)
  @observable
  private children = ""; // Example: "1_0, 1_7" (represents a child and their age)
  @observable
  private infantsInSeats = "";
  @observable
  private backTracking = {
    isProgrammatic: false,
    dialog: ""
  };

  @observable
  private origin = "";

  @observable
  private destination = "";

  @observable
  private originId: string;

  @observable
  private ftla: string;

  @observable
  private ttla: string;

  @observable
  private regionId = "";

  @observable
  private submitButtonClicked = false;

  @observable
  private destinationFieldProperties: FormFieldModel;

  @observable
  private isCarAdded = false;

  @observable
  private isFlightAdded = false;

  @observable
  private shouldSaveData: boolean;

  @observable
  private airlineChildSeating: AirlineChildSeatingOptions;

  @observable
  private packageType: FormFieldModel["packageType"];

  @observable
  private originFieldProperties: FormFieldModel;

  @observable
  private isDirectFlightOnly: boolean;

  @observable
  private shouldShowExpandedWizardInHsr = false;

  @observable
  private savedData: SavedWizardInputs;

  @observable
  private direct = "";

  @observable
  private wizardContainerIsOutOfWindow = false;

  // will auto open the new date picker, given the right experiment and desktop
  @observable
  private newDatePickerIsOpen = true;

  private focusCheckIn: React.RefObject<HTMLButtonElement>;
  private focusCheckOut: React.RefObject<HTMLButtonElement>;
  private focusGuestPicker: React.RefObject<HTMLButtonElement>;
  private wizardFormRef: React.RefObject<HTMLFormElement>;
  private wizardRef: React.RefObject<HTMLDivElement>;

  private essAnalyticsLogger: EssClientLogger;

  private shouldRouteToHsrFromCalendar = false;
  private showDestinationField = false;

  private isLargeScreenAndStickWhenWillExit: boolean;

  public state: Readonly<WizardHotelPWAState> = {
    startInvalidMessage: "",
    endInvalidMessage: ""
  };

  constructor(props: WizardHotelPWAProps) {
    super(props);
    const { startDate, context, flexComponent, cookies } = props;

    this.shouldSaveData = !!flexComponent.model.shouldRememberSelections;

    if (this.shouldSaveData) {
      this.savedData = JSON.parse(cookies.getCookie(CookieKeys.WIZARD_HOTEL)!);
    }

    if (flexComponent.model.showFlightDirectOnlyOption) {
      this.isDirectFlightOnly = false;
    }

    this.airlineChildSeating = AirlineChildSeatingOptions.ON_LAP;
    this.guestsVal = this.props.l10n.formatText(locKeys.singleRoomMultipleGuests, this.adults);
    this.showDestinationField = !!flexComponent.model.showDestinationField;

    this.setInitialDates(startDate);

    if (this.savedData) {
      this.getSavedDataFromCookie(props);
    }

    this.destinationFieldProperties = SearchForm.HotelOnly;
    this.originFieldProperties = SearchForm.HotelFlight;
    // if there is no location search context, there should be a destination field otherwise you can't search
    if (!context.searchContext.location) {
      this.showDestinationField = true;
    }

    if (!this.showDestinationField) {
      this.destination = context.searchContext.location.extendedName;
      this.regionId = context.searchContext.location.id;
      this.shouldRouteToHsrFromCalendar = true;
    }

    this.focusCheckIn = React.createRef();
    this.focusCheckOut = React.createRef();
    this.focusGuestPicker = React.createRef();
    this.wizardFormRef = React.createRef();
    this.wizardRef = React.createRef();

    if (isVariantEnabled(context, Experiment.PWA_ESS_Feedback_Loop_Enable)) {
      this.essAnalyticsLogger = new EssClientLogger(
        props.analytics,
        ClientLogger,
        this.destination
      );
    }
  }

  /**
   * Sets the initial wizard interval that our users will start and end their trip.
   * @param context - Required to pass to isVariantEnabled to access the experiments
   * @param startDate - The passed start date that the user will by default start
   *  their trip dates with
   */
  private setInitialDates(startDate: Date | undefined): void {
    // If we already have the start date, the interval is between that and a day more
    if (startDate) {
      const dayAfter = new Date(startDate);

      // Control (today and tomorrow)
      dayAfter.setDate(startDate.getDate() + 1);
      this.startDate = formatDate(startDate, { raw: "yyyy-MM-dd" });
      this.endDate = formatDate(dayAfter, { raw: "yyyy-MM-dd" });

      return;
    }

    const { context } = this.props;

    // Variant 2: No initial dates / no button
    if (isVariantEnabled(context, Experiment.Blossom_Date_Intervals_Mobile_HTG, 2)) {
      return;
    }

    const today = startDate ? startDate : new Date();

    // Variant 1: interval 30 days in the future
    if (isVariantEnabled(context, Experiment.Blossom_Date_Intervals_Mobile_HTG, 1)) {
      today.setDate(today.getDate() + 30);
    }

    const newEnd = new Date(today);

    // Control without startDate (today and tomorrow)
    newEnd.setDate(today.getDate() + 1);
    this.startDate = formatDate(today, { raw: "yyyy-MM-dd" });
    this.endDate = formatDate(newEnd, { raw: "yyyy-MM-dd" });
  }

  public componentDidMount() {
    // For new desktop datepicker and non-storefront page, we don't want default dates
    if (!this.showDestinationField && Viewport.screenSizeStore.viewportSize >= ViewSizes.LARGE) {
      this.startDate = "";
      this.endDate = "";
    }

    window.addEventListener("popstate", this.listenToBackButton);
    window.addEventListener("scroll", this.onScroll);
  }

  public componentWillUnmount() {
    window.removeEventListener("popstate", this.listenToBackButton);
    window.removeEventListener("scroll", this.onScroll);
  }

  private listenToBackButton = () => {
    //wizard.hotel.back-button
    const datepickerTrackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel",
      action: Action.BACK_BUTTON
    };

    //wizard.hotel.guests.back-button
    const guestsTrackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel.guests",
      action: Action.BACK_BUTTON
    };

    if (!this.backTracking.isProgrammatic && this.backTracking.dialog !== "") {
      const trackingInfo =
        this.backTracking.dialog === WizardDialogTrigger.GUESTPICKER
          ? guestsTrackingInfo
          : datepickerTrackingInfo;
      sendDelayedTrackEvent(trackingInfo, this.props.analytics);
      this.backTracking.dialog = "";
    }
    this.backTracking.isProgrammatic = false;
  };

  public openDestinationTypeaheadDialog = () => {
    if (this.props.routeStore) {
      this.props.routeStore.updateSearch({ pwaDialog: "destination" });
    }
  };

  public openOriginTypeaheadDialog = () => {
    if (this.props.routeStore) {
      this.props.routeStore.updateSearch({ pwaDialog: "origin" });
    }
  };

  public getSortFilterValue = (context: ExtendedContextStore) => {
    const { searchContext } = context;
    const isAirport = searchContext.location && searchContext.location.type === PageType.AIRPORT;
    const isPOI = searchContext.location && searchContext.location.type === PageType.POI;
    const sortFilterValue = isAirport || isPOI ? SORT_DISTANCE_VALUE : "";

    return sortFilterValue;
  };

  @action
  public updateDestinationField = (selection: TypeaheadSelection) => {
    const { data, term } = selection as TypeaheadSelectionProps;
    this.destination = term;
    this.regionId = (data && data.regionId) || "";
    this.ttla =
      data &&
      data.hierarchyInfo &&
      data.hierarchyInfo.airport &&
      data.hierarchyInfo.airport.airportCode;
  };

  @action
  public updateOriginField = (selection: TypeaheadSelection) => {
    const { data, term } = selection as TypeaheadSelectionProps;
    this.origin = term;
    this.originId = data.hierarchyInfo.airport.airportId;
    this.ftla = data.hierarchyInfo.airport.airportCode;
  };

  @action
  public clickDatePicker = (type: UitkDatePickerTriggeredSelector) => {
    const datepickerRfrrType = type === "start" ? "check-in" : "check-out";

    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      action: Action.CLICK,
      rfrr: `hotel.${datepickerRfrrType}`,
      linkName: "Datepicker dialog action"
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);

    if (this.props.routeStore) {
      this.props.routeStore.updateSearch({ pwaDialog: `datepicker-${type}` });
    }
    this.backTracking.dialog =
      type === "start" ? WizardDialogTrigger.DATEPICKERSTART : WizardDialogTrigger.DATEPICKEREND;
  };

  @action
  public closeDialog = (dialogType?: WizardDialogTrigger) => {
    if (this.props.routeStore) {
      this.props.routeStore.goBack();
    }

    let focus;
    switch (dialogType) {
      case WizardDialogTrigger.DATEPICKERSTART:
        focus = this.focusCheckIn;
        break;
      case WizardDialogTrigger.DATEPICKEREND:
        focus = this.focusCheckOut;
        break;
      case WizardDialogTrigger.GUESTPICKER:
        focus = this.focusGuestPicker;
        break;
    }

    if (focus) {
      const focusInput = focus.current;
      if (focusInput && typeof focusInput.focus === "function") {
        focusInput.focus();
      }
    }
    this.backTracking.isProgrammatic = true;
    this.backTracking.dialog = "";
  };

  @action
  public changeAddCar = (event: React.ChangeEvent<HTMLFormElement>) => {
    this.isCarAdded = event.currentTarget.checked;
    this.changeForm();
  };

  @action
  public changeAddFlight = (event: React.ChangeEvent<HTMLFormElement>) => {
    this.isFlightAdded = event.currentTarget.checked;
    this.changeForm();
  };

  @action
  public changeAirlineChildSeating = (event: React.ChangeEvent<HTMLFormElement>) => {
    this.airlineChildSeating = event.currentTarget.value;
    this.infantsInSeats =
      this.airlineChildSeating === AirlineChildSeatingOptions.IN_SEAT ? "1" : "";
  };

  /**
   * changeForm is triggered by AddCar and AddFlight checkbox component
   * It toggles between packages and hotel only search form
   */
  @action
  public changeForm = () => {
    if (this.isCarAdded && this.isFlightAdded) {
      this.originFieldProperties = SearchForm.HotelFlightCar;
      this.packageType = SearchForm.HotelFlightCar.packageType;
    } else if (this.isCarAdded) {
      this.destinationFieldProperties = SearchForm.HotelCar;
      this.packageType = SearchForm.HotelCar.packageType;
    } else if (this.isFlightAdded) {
      this.originFieldProperties = SearchForm.HotelFlight;
      this.packageType = SearchForm.HotelFlight.packageType;
    } else {
      this.destinationFieldProperties = SearchForm.HotelOnly;
      this.packageType = undefined;
    }
  };

  @computed
  get isPackage() {
    return this.isCarAdded || this.isFlightAdded;
  }

  @computed
  get totalTravelers() {
    const adultsArr = this.adults.split(",");
    let totalTravelers = this.children === "" ? 0 : this.children.split(",").length;
    adultsArr.forEach(el => (totalTravelers += Number(el)));

    return totalTravelers;
  }

  @computed
  get isPackageEnabled() {
    return (
      this.props.flexComponent.model.enableAddCar === true ||
      this.props.flexComponent.model.enableAddFlight === true
    );
  }

  @computed
  get disablePackageOptions() {
    if (this.totalTravelers > 6 && !this.validate("travelers")) {
      return true;
    }

    return this.adults.split(",").length > 6;
  }

  @action
  public changeDirectFlights = (event: React.ChangeEvent<HTMLFormElement>) => {
    this.isDirectFlightOnly = event.currentTarget.checked;
    this.direct = this.isDirectFlightOnly ? "1" : "";
  };

  @action
  public clickGuests = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      action: Action.CLICK,
      rfrr: "hotel.guests.open",
      linkName: "Guests dialog action"
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
    if (this.props.routeStore) {
      this.props.routeStore.updateSearch({ pwaDialog: "guestdialog" });
    }
    this.backTracking.dialog = WizardDialogTrigger.GUESTPICKER;
  };

  @action
  public saveGuests = (adults: string, children: string, displaySummary?: string) => {
    this.adults = adults;
    this.children = children;

    if (displaySummary) {
      this.guestsVal = displaySummary;
    }

    this.closeDialog(WizardDialogTrigger.GUESTPICKER);
  };

  @action
  public saveDates = (startDate: Date, endDate: Date, dateType: WizardDialogTrigger) => {
    this.startDate = formatDate(startDate, { raw: "yyyy-MM-dd" });
    this.endDate = formatDate(endDate, { raw: "yyyy-MM-dd" });

    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel.done-btn",
      action: Action.CLICK,
      linkName: "Datepicker Done action"
    };

    sendImmediateTrackEvent(trackingInfo, this.props.analytics);

    if (this.shouldRouteToHsrFromCalendar) {
      this.shouldShowExpandedWizardInHsr = true;

      // Using setTimeout to add form submission to a "queue" in order to wait for dates to update
      setTimeout(() => {
        if (
          this.wizardFormRef &&
          this.wizardFormRef.current &&
          this.validateAndGetFormErrors().length === 0
        ) {
          this.wizardFormRef.current.submit();
        } else {
          this.closeDialog(dateType);
        }
      }, 0);
    } else {
      this.closeDialog(dateType);
    }
  };

  @action
  public closeGuestPicker = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel.guests.close",
      action: Action.CLICK,
      linkName: "Guests dialog action"
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
    this.closeDialog(WizardDialogTrigger.GUESTPICKER);
  };

  @action
  public closeDatePicker = (dateType: WizardDialogTrigger) => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel.close",
      action: Action.CLICK,
      linkName: "Datepicker dialog action"
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
    this.closeDialog(dateType);
  };

  @action
  public onScroll = () => {
    const wizardWasOutOfWindow = this.wizardContainerIsOutOfWindow;
    if (this.wizardRef && this.wizardRef.current) {
      const currentWizardRef = this.isLargeScreenAndStickWhenWillExit
        ? this.wizardRef.current.getBoundingClientRect().top
        : this.wizardRef.current.getBoundingClientRect().bottom;
      this.wizardContainerIsOutOfWindow = currentWizardRef < 0;
    }

    // this prevent the new date picker to stay opened when switching between sticky/non-sticky wizard
    if (wizardWasOutOfWindow !== this.wizardContainerIsOutOfWindow) {
      this.newDatePickerIsOpen = false;
    }
  };

  @action
  public toggleNewDatePicker() {
    this.newDatePickerIsOpen = !this.newDatePickerIsOpen;
  }

  @action
  public onNewDatePickerOpen = (dateType: UitkNewDatePickerTriggeredSelector) => {
    this.newDatePickerIsOpen = true;

    const rfrr = dateType === UitkNewDatePickerTriggeredSelector.START ? "check-in" : "check-out";
    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      action: Action.CLICK,
      rfrr: `hotel.${rfrr}`,
      linkName: "Datepicker dialog action"
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
  };

  public onNewDatePickerDismiss = (isMobile: boolean, noUpdate: boolean) => {
    if (!noUpdate) {
      this.toggleNewDatePicker();
    }

    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel.close",
      action: Action.CLICK,
      linkName: "Datepicker dialog action"
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
  };

  public onNewDatePickerSubmit = (startDate: Date, endDate: Date, isMobile: boolean = false) => {
    this.toggleNewDatePicker();

    if (startDate || endDate) {
      this.startDate = formatDate(startDate, { raw: "yyyy-MM-dd" });
      this.endDate = formatDate(endDate, { raw: "yyyy-MM-dd" });
      this.validateAndGetFormErrors();
    }

    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel.done-btn",
      action: Action.CLICK,
      linkName: "Datepicker Done action"
    };

    sendImmediateTrackEvent(trackingInfo, this.props.analytics);
  };

  private shouldSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (
      (isVariantEnabled(this.props.context, Experiment.Blossom_Date_Intervals_Mobile_HTG, 2) &&
        !this.startDate) ||
      !this.endDate
    ) {
      e.preventDefault();
      this.clickDatePicker(UitkDatePickerTriggeredSelector.START);
    }

    if (this.validateAndGetFormErrors().length > 0) {
      e.preventDefault();
    }
  };

  public render() {
    const flexComponent = this.props.flexComponent;
    const pageHeading =
      this.props.compositionStore &&
      this.props.compositionStore.composition &&
      this.props.compositionStore.composition.pageHeading;

    const datePickerDialogProps = {
      endDate: this.endDate,
      startDate: this.startDate,
      onSave: this.saveDates,
      onDismiss: this.closeDatePicker
    };

    const guestDialogProps: GuestDialogProps = {
      adults: this.adults,
      children: this.children,
      moduleName: this.getModuleNameForTracking(),
      onDismiss: this.closeGuestPicker,
      onSave: this.saveGuests,
      l10n: this.props.l10n,
      isPackage: this.isPackage
    };
    const mainTitleWizardClassNames = "uitk-cell s-cell-1-1 m-cell-3-4 l-cell-3-4 xl-cell-3-4";

    const WizardForm = this.WizardForm;

    return (
      <div
        id={flexComponent.id}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
        className="WizardHotelPWA uitk-cell uitk-grid"
        ref={this.wizardRef}
      >
        <div className="WizardHotelPWA-wrapper uitk-cell">
          {flexComponent.model.includePageHeading && (
            <h1 className={`${mainTitleWizardClassNames} uitk-type-600 titlePage`}>
              {pageHeading}
            </h1>
          )}
          <Viewport context={this.props.context}>
            <ViewSmall>
              <WizardForm isMobile={true} isTablet={true} />
            </ViewSmall>
            <ViewMedium>
              <WizardForm isMobile={false} isTablet={true} />
            </ViewMedium>
            <ViewLarge>
              <WizardForm isMobile={false} isTablet={false} />
            </ViewLarge>
          </Viewport>
          <QueryRoute query={{ pwaDialog: `datepicker-start` }}>
            <Layer id="datepicker-start-dialog">
              <DatePickerDialog
                {...datePickerDialogProps}
                dateType={UitkDatePickerTriggeredSelector.START}
              />
            </Layer>
          </QueryRoute>
          <QueryRoute query={{ pwaDialog: `datepicker-end` }}>
            <Layer id="datepicker-end-dialog">
              <DatePickerDialog
                {...datePickerDialogProps}
                dateType={UitkDatePickerTriggeredSelector.END}
              />
            </Layer>
          </QueryRoute>
          <QueryRoute query={{ pwaDialog: "guestdialog" }}>
            <Layer id="guestpicker-dialog">
              <GuestDialog {...guestDialogProps} />
            </Layer>
          </QueryRoute>
        </div>
      </div>
    );
  }

  // Not its own component as it is not yet known what the best way to refactor the wizard is yet.
  // We need both isMobile and isTablet, because the sticky wizard is for tablet and desktop, and the new
  // date picker is for desktop only.
  private WizardForm = observer(
    ({ isMobile, isTablet }: { isMobile: boolean; isTablet: boolean }) => {
      const { context, flexComponent } = this.props;

      const hsrFilterCode = getHSRFilterCode(context);
      const hsrFilters = getHSRFilters(context);
      const hsrStarRatingCode = getHSRStarRatingCode(context);
      const propertyType = getPropertyTypeCode(context);

      const hiddenSearchDataProps: HiddenSearchDataProps = {
        langId: context.langId,
        children: this.children,
        adults: this.adults,
        infantsInSeats: this.infantsInSeats,
        endDate: this.endDate,
        startDate: this.startDate,
        regionId: this.regionId,
        packageType: this.packageType,
        originId: this.originId,
        ttla: this.ttla,
        ftla: this.ftla,
        direct: this.direct,
        expanded: this.shouldShowExpandedWizardInHsr,
        hsrFilterCode,
        hsrFilters,
        starRating: hsrStarRatingCode,
        isPackage: this.isPackage,
        sort: this.getSortFilterValue(context),
        propertyType
      };

      //Logic to determine if the wizard should be sticky
      const isSticky = this.wizardContainerIsOutOfWindow && !isMobile && !this.showDestinationField;
      const isStickyMobileEnabled = isMobile && !!this.props.flexComponent.model.showStickyWizard;

      // Logic to determine where the submit button should be placed within the rendered elements
      const shouldSubmitButtonBeLast = this.isFlightAdded || isMobile;

      // Use the new date picker only on desktop and non-storefront page
      const useNewDatePicker = !isMobile && !isTablet && !this.showDestinationField;

      const model = flexComponent && flexComponent.model;
      const stickWhen = model && model.stickWhen;
      const showStickyCTA = model && model.showStickyCTA;

      const isLargeScreen = !(isMobile || isTablet);

      this.isLargeScreenAndStickWhenWillExit =
        isLargeScreen && stickWhen === StickWhenType.WILL_EXIT;

      let dateFieldsClassNames = "uitk-cell s-cell-1-2 m-cell-1-4 l-cell-1-4 xl-cell-1-4";
      let guestFieldClassNames = "uitk-cell s-cell-1-1 m-cell-1-3 l-cell-1-3 xl-cell-1-3";
      let submitButtonClassNames = "uitk-cell s-cell-1-1 m-cell-1-6 l-cell-1-6 xl-cell-1-6";

      const mainFormWizardClassNames = (): string => {
        if (isSticky) {
          let className = "sticky";

          if (this.isLargeScreenAndStickWhenWillExit) {
            className += " stickyWillExit";
          }

          return className;
        } else {
          let className = "uitk-cell s-cell-1-1 m-cell-1-1 l-cell-1-1 xl-cell-1-1";

          if (this.isLargeScreenAndStickWhenWillExit) {
            className += " absolute";
          }

          return className;
        }
      };

      if (this.showDestinationField) {
        dateFieldsClassNames = "uitk-cell s-cell-1-2 m-cell-1-4 l-cell-1-6 xl-cell-1-6";
        guestFieldClassNames = "uitk-cell s-cell-1-1 m-cell-1-4 l-cell-1-6 xl-cell-1-6";
        submitButtonClassNames = "uitk-cell s-cell-1-1 m-cell-1-4 l-cell-1-6 xl-cell-1-6";
      }

      if (shouldSubmitButtonBeLast) {
        dateFieldsClassNames = "uitk-cell s-cell-1-2 m-cell-1-3 l-cell-1-6 xl-cell-1-6";
        guestFieldClassNames = "uitk-cell s-cell-1-1 m-cell-1-3 l-cell-1-6 xl-cell-1-6";
        submitButtonClassNames = "uitk-cell s-cell-1-1 m-cell-1-1 l-cell-1-6 xl-cell-1-6";
      }

      const stickyCTA = this.props.l10n.formatText(locKeys.stickyWizardTitle);
      const wizardFormTestId = isSticky ? "wizard-sticky" : "wizard";
      const mainTitleWizardClassNames = "uitk-cell s-cell-1-1 m-cell-3-4 l-cell-3-4 xl-cell-3-4";
      const includeStorefrontMultiLOBTitle =
        this.props.flexComponent.model.includeStorefrontMultiLOBTitle &&
        this.props.flexComponent.model.includeStorefrontMultiLOBTitle === true;

      // showStickyCTA is a new field in FM and should have a default value of undefined
      const shouldShowStickyCTA = isSticky && (showStickyCTA || showStickyCTA === undefined);

      const innerFormContainerClassNames = (): string => {
        let className = "uitk-grid all-x-gutter-three all-y-padding-three";

        className += this.isLargeScreenAndStickWhenWillExit
          ? " all-r-padding-three centerWizard"
          : " all-y-gutter-three";

        return className;
      };

      return (
        <>
          {isStickyMobileEnabled && (
            <StickyCTAMobile
              isSticky={this.wizardContainerIsOutOfWindow}
              moduleName={this.getModuleNameForTracking()}
              submitButtonText={this.props.l10n.formatText(locKeys.stickyWizardCallToAction)}
              scrollTarget={this.wizardFormRef}
            />
          )}
          <form
            noValidate={true}
            action={this.isPackage ? "/flexibleshopping" : "/Hotel-Search"}
            onSubmit={this.shouldSubmit}
            ref={this.wizardFormRef}
            autoComplete="off"
            className={mainFormWizardClassNames()}
            data-testid={wizardFormTestId}
          >
            {includeStorefrontMultiLOBTitle && (
              <Viewport>
                <ViewSmall>
                  <div className="uitk-grid all-t-padding-three">
                    <h1 className="uitk-cell uitk-type-heading-600">
                      {this.props.l10n.formatText(locKeys.mobileLocToken)}
                    </h1>
                  </div>
                </ViewSmall>
                <ViewMedium />
              </Viewport>
            )}
            <HiddenSearchData {...hiddenSearchDataProps} />
            {shouldShowStickyCTA && (
              <h1 className={`${mainTitleWizardClassNames} uitk-type-500`}>{stickyCTA}</h1>
            )}
            <div className={innerFormContainerClassNames()}>
              {this.showDestinationField && (
                <div
                  className={`uitk-cell s-cell-1-1 m-cell-1-1
              ${shouldSubmitButtonBeLast ? "l-cell-1-2 xl-cell-1-2" : "l-cell-1-3 xl-cell-1-3"}`}
                >
                  <DestinationField
                    formFieldModel={this.destinationFieldProperties}
                    openDialog={this.openDestinationTypeaheadDialog}
                    closeDialog={this.closeDialog}
                    updateDestinationField={this.updateDestinationField}
                    locale={context.locale}
                    guid={context.guid}
                    siteId={context.site.id}
                    invalid={this.validate("destination")}
                    destination={this.destination}
                    l10n={this.props.l10n}
                    essAnalyticsLogger={this.essAnalyticsLogger}
                  />
                </div>
              )}
              {useNewDatePicker ? (
                <NewDatePicker
                  startDate={this.startDate}
                  endDate={this.endDate}
                  isOpen={this.newDatePickerIsOpen}
                  onOpen={this.onNewDatePickerOpen}
                  onDismiss={this.onNewDatePickerDismiss}
                  onSubmit={this.onNewDatePickerSubmit}
                  startInvalidMessage={this.state.startInvalidMessage}
                  endInvalidMessage={this.state.endInvalidMessage}
                />
              ) : (
                <>
                  <div className={dateFieldsClassNames}>
                    <DateFields
                      dateType="start"
                      id="hotels-check-in"
                      date={this.startDate}
                      open={this.clickDatePicker}
                      invalid={this.state.startInvalidMessage}
                      focusInput={this.focusCheckIn}
                      l10n={this.props.l10n}
                    />
                  </div>
                  <div className={dateFieldsClassNames}>
                    <DateFields
                      dateType="end"
                      id="hotels-check-out"
                      date={this.endDate}
                      open={this.clickDatePicker}
                      invalid={this.state.endInvalidMessage}
                      focusInput={this.focusCheckOut}
                      l10n={this.props.l10n}
                    />
                  </div>
                </>
              )}
              <div className={guestFieldClassNames}>
                <GuestField
                  guestsVal={this.guestsVal}
                  open={this.clickGuests}
                  focusInput={this.focusGuestPicker}
                  l10n={this.props.l10n}
                  invalid={this.validate("travelers")}
                />
              </div>
              {!shouldSubmitButtonBeLast &&
                // Variant 2 has no initial dates and no submit button
                !isVariantEnabled(
                  this.props.context,
                  Experiment.Blossom_Date_Intervals_Mobile_HTG,
                  2
                ) && (
                  <div className={submitButtonClassNames}>
                    <SubmitButton
                      moduleName={this.getModuleNameForTracking()}
                      context={context}
                      submitButtonText={this.getSubmitButtonText()}
                    />
                  </div>
                )}
              {this.isPackageEnabled && (
                <div className="uitk-cell all-cell-1-1">
                  <AddFlight
                    flexComponent={this.props.flexComponent}
                    isFlightAdded={this.isFlightAdded}
                    changeFormProperties={this.changeAddFlight}
                    l10n={this.props.l10n}
                    disablePackageOptions={this.disablePackageOptions}
                  />
                  <AddCar
                    flexComponent={this.props.flexComponent}
                    isCarAdded={this.isCarAdded}
                    changeFormProperties={this.changeAddCar}
                    l10n={this.props.l10n}
                    disablePackageOptions={this.disablePackageOptions}
                  />
                  <PackageDisabledMessage
                    disablePackageOptions={this.disablePackageOptions}
                    l10n={this.props.l10n}
                    isPackageEnabled={this.isPackageEnabled}
                    totalTravelers={this.totalTravelers}
                  />
                </div>
              )}
              <OriginField
                formFieldModel={this.originFieldProperties}
                openDialog={this.openOriginTypeaheadDialog}
                closeDialog={this.closeDialog}
                updateOriginField={this.updateOriginField}
                locale={context.locale}
                guid={context.guid}
                siteId={context.site.id}
                invalid={this.validate("origin")}
                isFlightAdded={this.isFlightAdded}
                l10n={this.props.l10n}
                origin={this.origin}
                essAnalyticsLogger={this.essAnalyticsLogger}
              />
              <AirlineChildSeating
                isFlightAdded={this.isFlightAdded}
                airlineChildSeating={this.airlineChildSeating}
                changeAirlineChildSeating={this.changeAirlineChildSeating}
                isChildUnderTwoPresent={this.isChildUnderTwoPresent(this.children)}
                l10n={this.props.l10n}
              />
              {shouldSubmitButtonBeLast &&
                // Variant 2 has no initial dates and no submit button
                !isVariantEnabled(
                  this.props.context,
                  Experiment.Blossom_Date_Intervals_Mobile_HTG,
                  2
                ) && (
                  <div className={submitButtonClassNames}>
                    <SubmitButton
                      moduleName={this.getModuleNameForTracking()}
                      context={context}
                      submitButtonText={this.getSubmitButtonText()}
                    />
                  </div>
                )}
            </div>
          </form>
          {this.isLargeScreenAndStickWhenWillExit && (
            <form className="all-t-padding-twelve all-b-padding-six" />
          )}
        </>
      );
    }
  );

  private validate(key: string): string {
    const checkInDate = new Date(this.startDate);
    const checkOutDate = new Date(this.endDate);
    const destination = this.destination;
    const origin = this.origin;

    if (key === "startDate") {
      // In variant 2 of Blossom_Date_Intervals_Mobile_HTG, the users will have
      // no default start dates so we don't want to display an error
      if (
        !this.startDate &&
        isVariantEnabled(this.props.context, Experiment.Blossom_Date_Intervals_Mobile_HTG, 2)
      ) {
        return "";
      }

      if (!this.isValidDate(checkInDate)) {
        return this.props.l10n.formatText(locKeys.invalidFromDate);
      }
    }

    if (key === "endDate") {
      // In variant 2 of Blossom_Date_Intervals_Mobile_HTG, the users will have
      // no default start dates so we don't want to display an error
      if (
        !this.endDate &&
        isVariantEnabled(this.props.context, Experiment.Blossom_Date_Intervals_Mobile_HTG, 2)
      ) {
        return "";
      }

      if (!this.isValidDate(checkOutDate)) {
        return this.props.l10n.formatText(locKeys.invalidToDate);
      }

      if (this.isValidDate(checkInDate)) {
        const maxEndDate = new Date(
          checkInDate!.getFullYear(),
          checkInDate!.getMonth(),
          checkInDate!.getDate() + 28
        );

        if (checkOutDate < checkInDate) {
          return this.props.l10n.formatText(locKeys.invalidDateRangeEndBeforeStart);
        }

        if (checkOutDate > maxEndDate) {
          return this.props.l10n.formatText(locKeys.invalidDateRangeEndOverLimit);
        }
      }
    }

    if (key === "destination" && destination === "" && this.submitButtonClicked) {
      return this.props.l10n.formatText(locKeys.invalidDestination);
    }

    if (key === "origin" && this.isFlightAdded && origin === "" && this.submitButtonClicked) {
      return this.props.l10n.formatText(locKeys.invalidOrigin);
    }

    if (key === "travelers" && this.isPackage && this.totalTravelers > 6) {
      return this.props.l10n.formatText(locKeys.invalidNumberOfTravelersError);
    }

    return "";
  }

  public validateAndGetFormErrors = (event?: React.FormEvent<HTMLFormElement>) => {
    this.submitButtonClicked = true;
    const errors = [];
    const startDateError = this.validate("startDate");
    const endDateError = this.validate("endDate");
    const destinationError = this.validate("destination");
    const originError = this.validate("origin");
    const totalTravelerError = this.validate("travelers");

    this.setState({
      startInvalidMessage: startDateError,
      endInvalidMessage: endDateError
    });

    if (
      this.essAnalyticsLogger &&
      "function" === typeof this.essAnalyticsLogger.logWizardSubmitEvent
    ) {
      this.essAnalyticsLogger.logWizardSubmitEvent();
    }

    if (startDateError) {
      errors.push(startDateError);
    }
    if (endDateError) {
      errors.push(endDateError);
    }
    if (destinationError) {
      errors.push(destinationError);
    }
    if (originError) {
      errors.push(originError);
    }
    if (totalTravelerError) {
      errors.push(totalTravelerError);
    }

    // Saves the dates and guests in a cookie that expires in a day (default)
    if (this.shouldSaveData) {
      setCookie({
        key: CookieKeys.WIZARD_HOTEL,
        value: JSON.stringify({
          dest: this.props.flexComponent.model.showDestinationField
            ? encodeURIComponent(this.destination)
            : "",
          orgn: encodeURIComponent(this.origin),
          regn_id: this.regionId,
          org_id: this.originId,
          s_d: this.startDate.toString(),
          e_d: this.endDate.toString(),
          aCar: this.isCarAdded ? "true" : "false",
          aFlt: this.isFlightAdded ? "true" : "false",
          adlts: this.adults,
          chlrn: this.children,
          chld_st: this.infantsInSeats,
          d_flt: this.isDirectFlightOnly ? "true" : "false",
          p_type: this.packageType,
          ttla: this.ttla,
          ftla: this.ftla,
          direct: this.direct
        })
      });
    }

    if (errors.length > 0 && event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }

    return errors;
  };

  private isValidDate(date: Date) {
    return date && date.toString() !== "Invalid Date";
  }

  private getSavedDataFromCookie(props: WizardHotelPWAProps) {
    const { startDate, flexComponent } = props;

    const today = startDate ? startDate : new Date();
    const tomorrow = new Date(today.getTime());
    tomorrow.setDate(today.getDate() + 1);

    this.isCarAdded = this.savedData.aCar === "true";
    this.isFlightAdded = this.savedData.aFlt === "true";
    this.packageType = this.savedData.p_type ? this.savedData.p_type : undefined;

    // We want to make sure the startDate and end date are not in the past as cookies last a day
    const todayFormatted = formatDate(today, { raw: "yyyy-MM-dd" });
    const tomorrowFormatted = formatDate(tomorrow, { raw: "yyyy-MM-dd" });
    const holderStartDate = this.savedData.s_d;
    const holderEndDate = this.savedData.e_d;
    // Check that the startDate from the cookie is on or after today's date, otherwise change the date to today
    this.startDate =
      holderStartDate && holderStartDate >= todayFormatted ? holderStartDate : todayFormatted;
    // Check that the endDate from the cookie is a day or more after today's date, otherwise set it to tomorrow
    this.endDate =
      holderEndDate && holderEndDate > tomorrowFormatted ? holderEndDate : tomorrowFormatted;
    // In the case that the cookie's key is null, use default values
    this.adults = this.savedData.adlts || "2";
    this.children = this.savedData.chlrn || "";
    // Get the child seating options if there is a flight added and a child under age 2 is present
    this.infantsInSeats = this.savedData.chld_st || "";
    this.airlineChildSeating =
      this.infantsInSeats === "1"
        ? AirlineChildSeatingOptions.IN_SEAT
        : AirlineChildSeatingOptions.ON_LAP;
    // Split adults string into array of strings numbers ([2,1...])
    const adultsArr = this.adults.split(",");
    // Each index represents a room with adults, so we can get the total rooms
    const totalRooms = adultsArr.length;
    // Get the total guests by adding the children and adults
    const totalGuests = this.totalTravelers;
    // Determine which localization string to use
    if (totalRooms > 1) {
      this.guestsVal =
        totalGuests > 1
          ? this.props.l10n.formatText(locKeys.multipleRoomsMultipleGuests, totalRooms, totalGuests)
          : this.props.l10n.formatText(locKeys.multipleRoomsSingleGuest, totalRooms);
    } else {
      this.guestsVal =
        totalGuests > 1
          ? this.props.l10n.formatText(locKeys.singleRoomMultipleGuests, totalGuests)
          : this.props.l10n.formatText(locKeys.singleRoomSingleGuest);
    }
    this.destination = this.savedData.dest ? decodeURIComponent(this.savedData.dest) : "";
    this.origin = this.savedData.orgn ? decodeURIComponent(this.savedData.orgn) : "";
    this.regionId = this.savedData.regn_id || "";
    this.originId = this.savedData.org_id || "";
    this.ttla = this.savedData.ttla || "";
    this.ftla = this.savedData.ftla || "";
    this.direct = this.savedData.direct || "";

    if (flexComponent.model.showFlightDirectOnlyOption) {
      this.isDirectFlightOnly = this.savedData.d_flt === "true";
    }
  }

  private getModuleNameForTracking = () => {
    // append sticky to the module name if needed
    // doesn't look at the device information since you can't interact with the wizard it if you don't see it.
    return this.wizardContainerIsOutOfWindow
      ? `${this.props.flexComponent.name}-sticky`
      : this.props.flexComponent.name;
  };

  private getSubmitButtonText(): string {
    if (
      isVariantEnabled(this.props.context, Experiment.LOB_intent_search_button_text_hotels_pwa, 1)
    ) {
      if (this.isFlightAdded) {
        if (this.isCarAdded) {
          return this.props.l10n.formatText(locKeys.submitButtonWithHotelsFlightsAndCars);
        }

        return this.props.l10n.formatText(locKeys.submitButtonWithHotelsAndFlights);
      }

      if (this.isCarAdded) {
        return this.props.l10n.formatText(locKeys.submitButtonWithHotelsAndCars);
      }

      return this.props.l10n.formatText(locKeys.submitButton);
    } else {
      return this.props.l10n.formatText(locKeys.submitButton);
    }
  }

  private isChildUnderTwoPresent = (children: string) => {
    const regex = new RegExp("\\d+_[01]");
    const childrenSplit = children.split(",").filter(child => regex.test(child));
    if (childrenSplit.length) {
      return true;
    }

    return false;
  };
}

export default WizardHotelPWA;
